import {
    Box,
    Button,
    DialogContent,
    Divider,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import { DateRange, LocalizationProvider, StaticDateRangePicker } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import DialogAdaptive from './DialogAdaptive';
import StyledCardTitleBar from './StyledCardTitleBar';
import StyledDialogActions from './StyledDialogActions';

const BASE_OPTIONS: TimeRangeOption[] = [
    { value: 'custom', label: 'Custom', isCustom: true },
    { value: 'divider1', label: '', isDivider: true },
    { value: 'last24hours', label: 'Last 24 hours' },
    { value: 'last7days', label: 'Last 7 days' },
    { value: 'last30days', label: 'Last 30 days' },
    { value: 'last90days', label: 'Last 90 days' },
    { value: 'last180days', label: 'Last 180 days' },
    { value: 'last365days', label: 'Last 365 days' },
    { value: 'alltime', label: 'All time' },
];

interface TimeRangeOption {
    value: string;
    label: string;
    isCustom?: boolean;
    isDivider?: boolean;
}

interface DateRangeSelectProps {
    dateRange: DateRange<Moment>;
    value: string;
    onChange: (value: string, customRange?: DateRange<Moment>) => void;
    label?: string;
    limitStartDate?: Moment;
    limitEndDate?: Moment;
}

const DateRangeSelect: React.FC<DateRangeSelectProps> = ({
    dateRange,
    value,
    onChange,
    label = 'Time Range',
    limitStartDate,
    limitEndDate,
}) => {
    const [customDialogOpen, setCustomDialogOpen] = useState(false);
    const [customStartDate, setCustomStartDate] = useState<Moment | null>(null);
    const [customEndDate, setCustomEndDate] = useState<Moment | null>(null);
    const [options, setOptions] = useState<TimeRangeOption[]>(BASE_OPTIONS);

    // Generate options based on date range and limit dates
    useEffect(() => {
        const generateOptions = () => {
            const baseOptions: TimeRangeOption[] = BASE_OPTIONS;

            const dynamicOptions: TimeRangeOption[] = [];

            // Use limit dates if provided, otherwise fall back to dateRange
            const startBound = limitStartDate || dateRange[0];
            const endBound = limitEndDate || dateRange[1];

            if (startBound && endBound) {
                const startYear = startBound.year();
                const endYear = endBound.year();
                const currentMoment = moment();

                // Add year options based on limit dates
                const minYear = Math.max(startYear, currentMoment.year() - 10); // Limit to last 10 years max
                const maxYear = Math.min(endYear, currentMoment.year());

                for (let year = maxYear; year >= minYear; year--) {
                    // Only add year if it's within the limit bounds
                    const yearStart = moment().year(year).startOf('year');
                    const yearEnd = moment().year(year).endOf('year');

                    if (
                        yearStart.isSameOrAfter(startBound, 'day') ||
                        yearEnd.isSameOrBefore(endBound, 'day') ||
                        (yearStart.isBefore(startBound, 'day') && yearEnd.isAfter(endBound, 'day'))
                    ) {
                        dynamicOptions.push({
                            value: `year-${year}`,
                            label: year.toString(),
                        });
                    }
                }

                // Add month options for current year only
                const currentYear = currentMoment.year();
                if (currentYear >= startYear && currentYear <= endYear) {
                    const months = moment.months();
                    const currentMonth = currentMoment.month();

                    // Determine the valid month range within the current year
                    const yearStartBound = moment.max(startBound, moment().year(currentYear).startOf('year'));
                    const yearEndBound = moment.min(endBound, moment().year(currentYear).endOf('year'));

                    const startMonthIndex = yearStartBound.year() === currentYear ? yearStartBound.month() : 0;
                    const endMonthIndex = yearEndBound.year() === currentYear ? Math.min(yearEndBound.month(), currentMonth) : currentMonth;

                    // Add divider before base options if we have dynamic options if there are months for the cuurrent year
                    if (endMonthIndex >= startMonthIndex) {
                        dynamicOptions.push({ value: 'divider2', label: '', isDivider: true });
                    }

                    // Add months from most recent to oldest within the valid range
                    for (let month = endMonthIndex; month >= startMonthIndex; month--) {
                        dynamicOptions.push({
                            value: `month-${currentYear}-${month}`,
                            label: months[month],
                        });
                    }
                }
            }

            const allOptions = [
                baseOptions[0], // Custom option
                ...baseOptions.slice(1), // The rest of the base options except the last one
                // Divider after base options
                ...(dynamicOptions.length > 0 ? [{ value: 'divider3', label: '', isDivider: true }] : []),
                ...dynamicOptions,
            ];

            setOptions(allOptions);
        };

        generateOptions();
    }, [dateRange, limitStartDate, limitEndDate]);

    const handleSelectChange = (event: SelectChangeEvent<string>) => {
        const selectedValue = event.target.value;

        if (selectedValue === 'custom') {
            setCustomDialogOpen(true);
        } else if (!selectedValue.startsWith('divider')) {
            // Create custom range for each selected value
            const customRange = createRangeForValue(selectedValue);
            onChange(selectedValue, customRange);
        }
    };

    // Create date range based on selected value
    const createRangeForValue = (value: string): DateRange<Moment> => {
        const now = moment();

        switch (value) {
            case 'last24hours':
                return [now.clone().subtract(24, 'hours'), now.clone()];
            case 'last7days':
                return [now.clone().subtract(7, 'days').startOf('day'), now.clone().endOf('day')];
            case 'last30days':
                return [now.clone().subtract(30, 'days').startOf('day'), now.clone().endOf('day')];
            case 'last90days':
                return [now.clone().subtract(90, 'days').startOf('day'), now.clone().endOf('day')];
            case 'last180days':
                return [now.clone().subtract(180, 'days').startOf('day'), now.clone().endOf('day')];
            case 'last365days':
                return [now.clone().subtract(365, 'days').startOf('day'), now.clone().endOf('day')];
            case 'alltime':
                return [null, null];
            default:
                // Handle year and month cases
                if (value.startsWith('year-')) {
                    const year = parseInt(value.split('-')[1]);
                    return [moment().year(year).startOf('year'), moment().year(year).endOf('year')];
                }
                if (value.startsWith('month-')) {
                    const [, year, month] = value.split('-');
                    return [
                        moment().year(parseInt(year)).month(parseInt(month)).startOf('month'),
                        moment().year(parseInt(year)).month(parseInt(month)).endOf('month'),
                    ];
                }
                return [now.clone().subtract(30, 'days'), now.clone()];
        }
    };

    const handleCustomDateSubmit = () => {
        if (customStartDate && customEndDate) {
            const customRange: DateRange<Moment> = [customStartDate, customEndDate];
            onChange('custom', customRange);
        }
        setCustomDialogOpen(false);
        setCustomStartDate(null);
        setCustomEndDate(null);
    };

    const handleCustomDateCancel = () => {
        setCustomDialogOpen(false);
        setCustomStartDate(null);
        setCustomEndDate(null);
    };

    // Properly typed onChange handlers for MobileDatePicker
    const handleDateChange = (value: DateRange<moment.Moment>) => {
        setCustomStartDate(value ? value[0] : null);
        setCustomEndDate(value ? value[1] : null);
    };

    const renderMenuItems = () => {
        const items: JSX.Element[] = [];

        options.forEach(option => {
            if (option.isDivider) {
                items.push(<Divider key={option.value} />);
                return;
            }

            if (option.isCustom) {
                items.push(
                    <MenuItem key={option.value} value={option.value}>
                        <ListItemText primary={option.label} />
                    </MenuItem>,
                );
                return;
            }

            // Regular menu items (including year and month options)
            items.push(
                <MenuItem key={option.value} value={option.value}>
                    <ListItemText primary={option.label} />
                </MenuItem>,
            );
        });

        return items;
    };

    return (
        <>
            <FormControl fullWidth variant="outlined">
                <InputLabel>{label}</InputLabel>
                <Select
                    value={value}
                    onChange={handleSelectChange}
                    label={label}
                    sx={{
                        backgroundColor: 'background.paper',
                        height: '40px',
                    }}
                >
                    {renderMenuItems()}
                </Select>
            </FormControl>

            <DialogAdaptive open={customDialogOpen} onClose={handleCustomDateCancel} maxWidth="xs" fullWidth>
                <StyledCardTitleBar title="Select Custom Date Range" />

                <DialogContent>
                    <Box display={'flex'} justifyContent="center" alignItems="center">
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <StaticDateRangePicker
                                defaultValue={[customStartDate, customEndDate]}
                                onChange={handleDateChange}
                                minDate={limitStartDate}
                                maxDate={limitEndDate}
                                slotProps={{
                                    // hide button bar
                                    actionBar: {
                                        actions: [],
                                    },
                                }}
                                sx={
                                    {
                                        // align center
                                    }
                                }
                            />
                        </LocalizationProvider>
                    </Box>
                </DialogContent>

                <StyledDialogActions>
                    <Button onClick={handleCustomDateCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleCustomDateSubmit} variant="contained" disabled={!customStartDate || !customEndDate}>
                        Apply
                    </Button>
                </StyledDialogActions>
            </DialogAdaptive>
        </>
    );
};

export default DateRangeSelect;
